<template>
  <header class="header">
    <div class="header__left">
      <button v-if="isSidebarCollapsed" class="header__sidebar-toggle" @click="$emit('toggle-sidebar')" :aria-label="isSidebarCollapsed ? 'Открыть меню' : 'Скрыть меню'">
        <img 
          src="@/assets/images/arrow-up.svg" 
          alt="Toggle menu" 
          class="header__toggle-icon"
          :class="{ 'rotated': isSidebarCollapsed }"
        />
      </button>
      <button class="header__back" @click="goBack" aria-label="Назад">
        <img src="@/assets/images/arrow-left.svg" alt="Назад" class="header__back-icon" />
      </button>
      <button class="header__title" @click="goHome" :title="title" aria-label="Перейти на главную">
        {{ title }}
      </button>
    </div>

    <div class="header__center">
      <div>{{ live ? nowText : dateText }}</div>
      <div v-if="live" class="header__shift" :title="shiftDetail">{{ shiftLabel }}</div>
    </div>

    <div class="header__right">
      <!-- Переключатель языков — по ТЗ 03.09.2026: казахский (основной) / русский -->
      <div class="header__lang">
        <button
          v-for="opt in langOptions"
          :key="opt.value"
          type="button"
          class="header__lang-btn"
          :class="{ 'is-active': $i18n.locale === opt.value }"
          :title="opt.label"
          @click="switchLocale(opt.value)"
        >{{ opt.short }}</button>
      </div>
      <button class="header__icon-btn" aria-label="Поиск"></button>
      <button class="header__icon-btn header__notif" aria-label="Уведомления">
        <img src="@/assets/images/bell.svg" alt="Уведомления">
        <!-- <span class="header__notif-dot" /> -->
      </button>
      <div v-if="userDisplay" class="header__user-display" :title="userDisplay">{{ userDisplay }}</div>
      <div class="header__user">
        <div class="header__avatar">{{ userInitials }}</div>
        <button class="header__logout-btn" @click="handleLogout" :title="$t('nav.logout')" :aria-label="$t('nav.logout')">
          <img class="header__chevron" src="@/assets/images/arrow-up.svg" alt="Выход" />
        </button>
      </div>
    </div>
  </header>
</template>

<script>
import { setLocale, SUPPORTED_LOCALES } from '@/main'

export default {
  name: 'HeaderBar',
  props: {
    title: { type: String, default: 'Принятие решения на очередные сутки' },
    dateText: { type: String, default: '' },
    live: { type: Boolean, default: true },
    isSidebarCollapsed: { type: Boolean, default: false },
    userName: { type: String, default: 'Иван Иванов' },
    userRole: { type: String, default: '' },
    userRank: { type: String, default: '' },
  },
  emits: ['toggle-sidebar'],
  data() {
    return {
      now: new Date(),
      _timer: null,
      langOptions: [
        { value: 'kz', short: 'ҚАЗ', label: 'Қазақша' },
        { value: 'ru', short: 'РУС', label: 'Русский' },
      ],
    }
  },
  computed: {
    isHome() {
      try { return this.$route?.path === '/' } catch { return false }
    },
    userDisplay() {
      const parts = [this.userRole, this.userName, this.userRank]
        .map((v) => String(v || '').trim())
        .filter(Boolean)
      return parts.join(' ')
    },
    userInitials() {
      if (!this.userName) return 'ИИ';
      const parts = this.userName.trim().split(/\s+/);
      if (parts.length >= 2) {
        return (parts[0][0] + parts[1][0]).toUpperCase();
      }
      return parts[0].substring(0, 2).toUpperCase();
    },
    nowText() {
      const d = this.now
      // По ТЗ 03.09.2026: день недели локализованный (kz — основной).
      const weekdayRU = ['воскресенье','понедельник','вторник','среда','четверг','пятница','суббота']
      const weekdayKZ = ['жексенбі','дүйсенбі','сейсенбі','сәрсенбі','бейсенбі','жұма','сенбі']
      const pad2 = (n) => String(n).padStart(2, '0')
      const DD = pad2(d.getDate())
      const MM = pad2(d.getMonth() + 1)
      const YYYY = String(d.getFullYear())
      const HH = pad2(d.getHours())
      const mm = pad2(d.getMinutes())
      const week = (this.$i18n?.locale === 'kz' ? weekdayKZ : weekdayRU)[d.getDay()]
      return `${DD}.${MM}.${YYYY}  ${HH}:${mm} ${week}`
    },
    // По ТЗ «Рабочие задачи» блок 5: индикатор текущей армейской смены (20:00→20:00).
    shift() {
      // Динамический импорт не нужен — модуль синхронный.
      // eslint-disable-next-line global-require
      const { describeShift } = require('@/api/shiftDate')
      return describeShift(this.now)
    },
    shiftLabel() {
      const s = this.shift
      const [y, m, d] = s.date.split('-')
      return `${this.$t('app.shift')}: ${d}.${m}`
    },
    shiftDetail() {
      return this.shift.label
    },
  },
  mounted() {
    if (this.live) {
      const update = () => { this.now = new Date() }
      const msToNextMinute = 60000 - (Date.now() % 60000)
      this._timer = setTimeout(() => {
        update()
        this._timer = setInterval(update, 60000)
      }, msToNextMinute)
    }
  },
  beforeUnmount() {
    if (this._timer) {
      clearTimeout(this._timer)
      try { clearInterval(this._timer) } catch (e) {}
      this._timer = null
    }
  },
  methods: {
    goHome() {
      if (!this.isHome) this.$router.push('/')
    },
    goBack() {
      if (window.history.length > 1) window.history.back(); else this.$router.push('/')
    },
    handleLogout() {
      // Очищаем токены
      import('@/middlewares/auth').then(({ logout }) => {
        logout();
        // Перенаправляем на страницу логина
        this.$router.push('/login');
      });
    },
    switchLocale(loc) {
      if (SUPPORTED_LOCALES.includes(loc)) setLocale(loc)
    },
  },
}
</script>

<style scoped>
.header {
  position: sticky;
  top: 0;
  /* Leaflet controls use high z-index; keep header above maps when scrolling */
  z-index: 2000;
  width: 100%;
  padding: 24px 32px;
  background: #ffffff;
  border-bottom: 1px solid #E9EAEC;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  box-sizing: border-box;
}

.header__left { display: flex; align-items: center; gap: 16px; min-width: 0; flex: 1 1 auto; }

.header__sidebar-toggle {
  width: 32px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: rgba(18, 32, 59, 0.08);
  border-radius: 6px;
  padding: 0;
  cursor: pointer;
  transition: background 0.2s ease;
}
.header__sidebar-toggle:hover {
  background: rgba(18, 32, 59, 0.12);
}

.header__toggle-icon {
  width: 18px;
  height: 18px;
  transform: rotate(-90deg);
  transition: transform 0.3s ease;
  filter: invert(10%);
}
.header__toggle-icon.rotated {
  transform: rotate(90deg);
}

.header__back { width: 32px; height: 32px; display: inline-flex; align-items: center; justify-content: center; border: none; background: transparent; padding: 0; cursor: pointer; }
.header__back-icon { width: 20px; height: 20px; filter: invert(40%); }
.header__title,
.header__center,
.header__user-display {
  font-family: 'Inter', sans-serif;
  font-size: 18px;
  font-weight: 600;
  line-height: 22px;
  letter-spacing: 0.1px;
  color: #111827;
}

.header__title {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  word-wrap: break-word;
  background: transparent;
  border: none;
  padding: 0;
  cursor: pointer;
  text-align: left;
}
.header__title:hover {
  text-decoration: underline;
}

.header__center {
  white-space: nowrap;
  word-wrap: break-word;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  line-height: 1.15;
}
.header__shift {
  font-size: 11px;
  color: #6B7280;
  background: #F3F4F6;
  padding: 1px 8px;
  border-radius: 8px;
  font-weight: 600;
  cursor: help;
}

.header__right { display: flex; align-items: center; justify-content: flex-end; gap: 24px; }
.header__user-display {
  max-width: 360px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.header__icon-btn { width: 32px; height: 32px; padding: 6px 12px; border: none; background: transparent; border-radius: 6px; cursor: pointer; }

/* Переключатель языков — kz/ru pill в шапке (по ТЗ 03.09.2026) */
.header__lang { display: inline-flex; gap: 2px; padding: 3px; background: #F3F4F6; border-radius: 8px; border: 1px solid #E5E7EB; margin-right: 10px; }
.header__lang-btn { padding: 4px 10px; border: 0; background: transparent; color: #6B7280; font-weight: 700; font-size: 12px; border-radius: 6px; cursor: pointer; font-family: inherit; letter-spacing: 0.4px; transition: background .12s, color .12s; }
.header__lang-btn:hover { color: #111827; }
.header__lang-btn.is-active { background: #2F78EE; color: #fff; box-shadow: 0 1px 3px rgba(47,120,238,.35); }
.header__notif { position: relative; }
.header__notif-dot { position: absolute; right: 4px; top: 2px; transform: translate(100%, 50%); width: 8px; height: 8px; background: #E03137; border-radius: 50%; outline: 2px solid #fff; pointer-events: none; }
.header__user { display: flex; align-items: center; gap: 8px; }
.header__avatar { 
  width: 32px; 
  height: 32px; 
  border-radius: 50%; 
  background: linear-gradient(135deg, #2F78EE 0%, #1E5BBF 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #FFFFFF;
  font-family: Arial, Helvetica, sans-serif;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.5px;
  user-select: none;
}
.header__logout-btn {
  width: 24px;
  height: 24px;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.2s ease;
}
.header__logout-btn:hover {
  transform: scale(1.1);
}
.header__chevron { width: 14px; height: 14px; transform: rotate(180deg); }
</style>